import { useMemo, useState } from 'react';

import { useCreateInstagramAccountMutation } from '@admin/mutations/InstagramAccountsMutations';

import { SuperAdminPromoter, useCityPromotersQuery } from '@queries/PromoterQueries';
import { useSeriesByTagId } from '@queries/TagQueries';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const ProducerItem = ({ promoter }: { promoter: SuperAdminPromoter }) => {
  const subtitle = useMemo(() => {
    const details = [];

    if (promoter.contact.instagram) {
      details.push(`@${promoter.contact.instagram}`);
    }

    return details.join(' | ');
  }, [promoter]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <div>
            <strong>{promoter.title}</strong>
          </div>
          {subtitle && <div style={{ fontSize: '.8rem', opacity: 0.8 }}>{subtitle}</div>}
        </div>
      </div>
    </div>
  );
};

export const SeriesProducersSection = ({ tagId }: { tagId: string }) => {
  const { series } = useSeriesByTagId(tagId);
  const { cityPromoters = [], refetchCityPromoters } = useCityPromotersQuery(series?.city?._id);
  const [searchValue, setSearchValue] = useState('');
  const [viewAll, setViewAll] = useState(false);
  const [instagramToAdd, setInstagramToAdd] = useState('');

  const activePromoters = useMemo(
    () => cityPromoters.filter((promoter) => promoter.isActive),
    [cityPromoters],
  );

  const filteredPromoters = useMemo(() => {
    if (viewAll) {
      return cityPromoters;
    }

    if (!searchValue) {
      return [];
    }

    const searchValueLower = searchValue.toLowerCase().trim();

    return cityPromoters.filter((promoter) =>
      promoter.title.toLowerCase().includes(searchValueLower),
    );
  }, [viewAll, searchValue, cityPromoters]);

  const { createInstagramAccountAsync, isSubmitting: isAddingInstagram } =
    useCreateInstagramAccountMutation();
  const handleAddInstagram = async () => {
    if (!instagramToAdd) {
      return;
    }

    await createInstagramAccountAsync({
      username: instagramToAdd,
      type: 'promoter',
      cityId: series?.city?._id,
    });

    setInstagramToAdd('');

    await refetchCityPromoters();
  };

  return (
    <>
      <TitleToolbar
        text="Producers"
        size="md"
        action={<>{`${activePromoters.length} active producers`}</>}
      />
      <div className="d-grid gap-2">
        <div className="d-flex gap-1 align-items-center">
          <div className="flex-1 w-100">
            <Input
              value={instagramToAdd}
              placeholder="create.ig.handle"
              onChange={(e) => setInstagramToAdd(e.target.value)}
            />
          </div>
          <div className="flex-0 h-100">
            <Button
              className="h-100"
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={handleAddInstagram}
            >
              {isAddingInstagram ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>
        <Input onChange={(e) => setSearchValue(e.target.value)} />
      </div>
      <div className="d-grid gap-2 mt-3">
        {filteredPromoters.map((promoter) => (
          <ProducerItem key={promoter._id} promoter={promoter} />
        ))}
      </div>
      <div>
        <Button variant="flat" color="neutral" onClick={() => setViewAll(!viewAll)}>
          {viewAll ? 'View Less' : 'View All'}
        </Button>
      </div>
    </>
  );
};
